import React from 'react';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { FDBodyText, FDStack } from '@facet-wealth/design';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import { ImageRel } from 'Packages/FacetCore/Experimental/RelationshipComponents/ImageRel';
import fr from 'facet-rest';
import useBoneBackgroundColor from 'Hooks/useBoneBackgroundColor';
import { BodyTextMedium, DisplayTextMedium, HeadlineTextMedium } from 'Packages/FacetDesign/Components';
import useMobileLayout from 'Hooks/useMobileLayout';
import { Richtext } from 'Packages/FacetDesign/Components/Richtext';
import ProfileSubpage from 'Views/Page/Content/Templates/Profile/Templates/ProfileSubpage';
import { dataAttrs } from 'Helpers/Testing';
import { EstatePlanningManagementPageFAQ } from './EstatePlanningManagementPageFAQ';
import { EstatePlanningManagementPageStructuredContent } from './EstatePlanningManagementPageStructuredContent';
import { EstatePlanningManagementPageSteps } from './EstatePlanningManagementPageSteps';

const metaOverrideStep = buildFacetDesignOverride()
  .nameOverride('title', {
    _component: () => DisplayTextMedium,
  }, overrideReadOnly)
  .nameOverride('subtitle', {
    _component: () => HeadlineTextMedium,
  }, overrideReadOnly)
  .nameOverride('description', {
    _component: () => BodyTextMedium,
  }, overrideReadOnly)
  .override({ name: 'description', hint: 'richtext' }, {
    _component: () => Richtext,
    _customProps: { Component: FDBodyText },
  }, overrideReadOnly)
  .nameOverride('steps', {
    _component: () => EstatePlanningManagementPageSteps,
  }, overrideReadOnly)
  .nameOverride('frequentlyAskedQuestions', {
    _component: () => EstatePlanningManagementPageFAQ,
  }, overrideReadOnly)
  .nameOverride('structuredContent', {
    _component: () => EstatePlanningManagementPageStructuredContent,
  }, overrideReadOnly);

export const EstatePlanningManagementPageTemplate = () => {
  const { pageContentResource } = usePageContentContext();
  const isMobile = useMobileLayout();

  useBoneBackgroundColor();

  return (
    <ProfileSubpage
      backLabel={pageContentResource.withRelationship(fr.relationships.householdMembershipPage.name, (rel) => {
        return rel.meta('prompt');
      }, () => undefined)}
      backLink={pageContentResource.withRelationship(fr.relationships.householdMembershipPage.name, (rel) => {
        return rel.link('related');
      }, () => null)}
    >
      <FDStack
        spacing={8}
        direction={'column'}
        paddingY={3}
        width={'100%'}
        {...dataAttrs('estate-planning-management-page')}
      >
        {isMobile
          ? pageContentResource.withRelationship(fr.relationships.imageMobile.name, (rel) => {
            return (
              <ImageRel relationship={rel} />
            );
          }, () => null)
          : pageContentResource.withRelationship(fr.relationships.image.name, (rel) => {
            return (
              <ImageRel relationship={rel} />
            );
          }, () => null)
        }

        <FDStack spacing={4} justifyContent={'space-between'} alignItems={'flex-start'}>
          <FDStack direction={'column'} spacing={8} flex={1} width={'100%'}>
            <FDStack direction={'column'} spacing={5}>
              <MetaControl resource={pageContentResource} attrName={'title'} metaOverrideSet={metaOverrideStep} />
              <MetaControl resource={pageContentResource} attrName={'subtitle'} metaOverrideSet={metaOverrideStep} />
              <MetaControl resource={pageContentResource} attrName={'description'} metaOverrideSet={metaOverrideStep} />
            </FDStack>
            {isMobile ? <MetaControl
              resource={pageContentResource}
              attrName={'steps'}
              metaOverrideSet={metaOverrideStep}
            /> : null}
            <MetaControl resource={pageContentResource} attrName={'frequentlyAskedQuestions'} metaOverrideSet={metaOverrideStep} />
            <MetaControl resource={pageContentResource} attrName={'structuredContent'} metaOverrideSet={metaOverrideStep} />
            {isMobile ? <MetaControl
              resource={pageContentResource}
              attrName={'steps'}
              metaOverrideSet={metaOverrideStep}
            /> : null}
            <MetaControl resource={pageContentResource} attrName={'disclosure'} metaOverrideSet={metaOverrideStep} />
          </FDStack>
          {isMobile ? null : <MetaControl
            resource={pageContentResource}
            attrName={'steps'}
            metaOverrideSet={metaOverrideStep}
          />}
        </FDStack>
      </FDStack>
    </ProfileSubpage>
  );
};
